import clsx from 'clsx';
import React, { ChangeEvent, useState } from 'react';
import { useForm } from '../../../../../hooks/useForm';
import { SettingItemActions } from '../../SettingItemActions';
import { Input } from '../../../../../components/Input';
import { Label } from '../../../../../components/Label';
import { Tooltip } from '../../../../../components/Tooltip';
import { SettingTag } from '../../SettingTag';

interface Props {
  index: number;
  transport: CreateTransportType;
  onRemove?(index: number): void;
  onSave?(item: CreateTransportType, index: number): void;
}

interface Content extends Props {
  editing: boolean;
}

interface Form extends Content {
  values: CreateTransportType;
  onChange(event: ChangeEvent<HTMLInputElement>): void;
  updateField(field: keyof CreateTransportType, value: any): void;
}

export const Transport = (props: Props) => {
  const [editing, setEditing] = useState(false);
  const { transport, index, onRemove = null, onSave = null } = props;
  const { values, onChange, updateField } = useForm<CreateTransportType>(
    transport,
    () => null
  );

  return (
    <div
      className={clsx('w-full inline-flex justify-between items-center gap-4')}
    >
      <EditTransport
        editing={editing}
        onChange={onChange}
        updateField={updateField}
        values={values}
        {...props}
      />
      <TransportContent editing={editing} {...props} />
      {onSave && onRemove ? (
        <SettingItemActions
          canSave={values.name.trim() !== ''}
          editing={editing}
          onEdit={() => setEditing(true)}
          onRemove={() => onRemove(index)}
          onSave={() => {
            onSave(values, index);
            setEditing(false);
          }}
          onCancel={() => setEditing(false)}
        />
      ) : null}
    </div>
  );
};

const EditTransport = ({ editing, values, onChange }: Form) => {
  const defaultProps: any = {
    variant: 'contained',
    onChange: onChange,
    required: true,
  };

  const numberInputProps: any = {
    type: 'number',
    min: 0,
    max: 30,
  };

  if (editing === false) return null;
  return (
    <fieldset className="inline-flex items-center gap-4">
      <Input name="name" label="Name" value={values.name} {...defaultProps} />
      <Input
        name="ingressHD"
        label="Ingress HD"
        value={values.ingressHD}
        {...numberInputProps}
        {...defaultProps}
      />
      <Input
        name="ingressSD"
        label="Ingress SD"
        value={values.ingressSD}
        {...numberInputProps}
        {...defaultProps}
      />
      <Input
        name="wanHD"
        label="WAN HD"
        value={values.wanHD}
        {...numberInputProps}
        {...defaultProps}
      />
      <Input
        name="wanSD"
        label="WAN SD"
        value={values.wanSD}
        {...numberInputProps}
        {...defaultProps}
      />
    </fieldset>
  );
};

const TransportContent = ({ editing, transport }: Content) => {
  if (editing) return null;

  return (
    <div className="inline-flex items-center gap-4 w-full">
      <Tooltip title="Transport Name">
        <SettingTag name={transport.name} color="teal" />
      </Tooltip>

      <div className="inline-flex items-center justify-between w-full px-4">
        <Label text="Ingress HD:">{transport.ingressHD}</Label>
        <Label text="Ingress SD:">{transport.ingressSD}</Label>
        <Label text="WAN HD:">{transport.wanHD}</Label>
        <Label text="WAN SD:">{transport.wanSD}</Label>
      </div>
    </div>
  );
};
